'use client'

import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { useSearchAll } from '@/lib/search/use-search-all'
import { PromptTypeaheadItem, JobTypeaheadItem, UserTypeaheadItem, TopicTypeaheadItem } from './search-typeahead-item'
import { SearchEmpty } from './search-empty'
import { useSearch } from './search-provider'
import { addRecentSearch } from './search-recent'
import type { PromptSearchResult, JobSearchResult, UserSearchResult, TopicSearchResult } from '@/lib/search/types'

interface SearchTypeaheadProps {
  query: string
}

type FlatItem =
  | { type: 'prompt'; data: PromptSearchResult }
  | { type: 'job'; data: JobSearchResult }
  | { type: 'user'; data: UserSearchResult }
  | { type: 'topic'; data: TopicSearchResult }

export function SearchTypeahead({ query }: SearchTypeaheadProps) {
  const { data, isLoading } = useSearchAll(query)
  const [activeIndex, setActiveIndex] = useState(-1)
  const router = useRouter()
  const { close } = useSearch()
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }

  // Build flat list for keyboard navigation (stable reference)
  const flatItems = useMemo<FlatItem[]>(() => {
    if (!data) return []
    return [
      ...data.topics.map((t) => ({ type: 'topic' as const, data: t })),
      ...data.prompts.map((p) => ({ type: 'prompt' as const, data: p })),
      ...data.jobs.map((j) => ({ type: 'job' as const, data: j })),
      ...data.users.map((u) => ({ type: 'user' as const, data: u })),
    ]
  }, [data])

  const navigateToItem = useCallback(
    (item: FlatItem) => {
      addRecentSearch(query)
      close()
      switch (item.type) {
        case 'topic':
          router.push(`/t/${encodeURIComponent(item.data.tag)}`)
          break
        case 'prompt':
          router.push(`/p/${item.data.usernameRef}/${item.data.slug}`)
          break
        case 'job':
          router.push(`/jobs/${item.data.slug}`)
          break
        case 'user':
          router.push(item.data.accountType === 'company' && item.data.companySlug ? `/company/${item.data.companySlug}` : `/u/${item.data.username}`)
          break
      }
    },
    [query, close, router],
  )

  // Use ref to avoid stale closures in keyboard handler
  const flatItemsRef = useRef(flatItems)
  flatItemsRef.current = flatItems
  const activeIndexRef = useRef(activeIndex)
  activeIndexRef.current = activeIndex

  useEffect(() => {
    setActiveIndex(-1)
  }, [query])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const items = flatItemsRef.current
      if (items.length === 0) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1))
      } else if (e.key === 'Enter' && activeIndexRef.current >= 0) {
        e.preventDefault()
        navigateToItem(items[activeIndexRef.current])
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [navigateToItem])

  if (query.trim().length < 2) return null

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--color-brand-indigo)] border-t-transparent" />
      </div>
    )
  }

  if (!data || flatItems.length === 0) {
    return <SearchEmpty query={query} />
  }

  let idx = 0

  return (
    <div className="max-h-[60vh] overflow-y-auto py-2">
      {data.topics.length > 0 && (
        <div>
          <p className="px-4 py-1.5 text-xs font-medium text-[var(--color-text-muted)]" style={font}>Topics</p>
          {data.topics.map((item) => {
            const currentIdx = idx++
            return (
              <Link
                key={item.tag}
                href={`/t/${encodeURIComponent(item.tag)}`}
                className="block w-full text-left"
                onClick={() => { addRecentSearch(query); close() }}
                onMouseEnter={() => setActiveIndex(currentIdx)}
              >
                <TopicTypeaheadItem item={item} isActive={activeIndex === currentIdx} />
              </Link>
            )
          })}
        </div>
      )}

      {data.prompts.length > 0 && (
        <div>
          <p className="px-4 py-1.5 text-xs font-medium text-[var(--color-text-muted)]" style={font}>Prompts</p>
          {data.prompts.map((item) => {
            const currentIdx = idx++
            return (
              <Link
                key={item.id}
                href={`/p/${item.usernameRef}/${item.slug}`}
                className="block w-full text-left"
                onClick={() => { addRecentSearch(query); close() }}
                onMouseEnter={() => setActiveIndex(currentIdx)}
              >
                <PromptTypeaheadItem item={item} isActive={activeIndex === currentIdx} />
              </Link>
            )
          })}
        </div>
      )}

      {data.jobs.length > 0 && (
        <div>
          <p className="px-4 py-1.5 text-xs font-medium text-[var(--color-text-muted)]" style={font}>Jobs</p>
          {data.jobs.map((item) => {
            const currentIdx = idx++
            return (
              <Link
                key={item.id}
                href={`/jobs/${item.slug}`}
                className="block w-full text-left"
                onClick={() => { addRecentSearch(query); close() }}
                onMouseEnter={() => setActiveIndex(currentIdx)}
              >
                <JobTypeaheadItem item={item} isActive={activeIndex === currentIdx} />
              </Link>
            )
          })}
        </div>
      )}

      {data.users.length > 0 && (
        <div>
          <p className="px-4 py-1.5 text-xs font-medium text-[var(--color-text-muted)]" style={font}>People</p>
          {data.users.map((item) => {
            const currentIdx = idx++
            const userHref = item.accountType === 'company' && item.companySlug
              ? `/company/${item.companySlug}`
              : `/u/${item.username}`
            return (
              <Link
                key={item.id}
                href={userHref}
                className="block w-full text-left"
                onClick={() => { addRecentSearch(query); close() }}
                onMouseEnter={() => setActiveIndex(currentIdx)}
              >
                <UserTypeaheadItem item={item} isActive={activeIndex === currentIdx} />
              </Link>
            )
          })}
        </div>
      )}

      {/* View all results link */}
      <div className="border-t border-[var(--color-border-default)] px-4 py-2">
        <Link
          href={`/search?q=${encodeURIComponent(query)}`}
          className="text-xs font-medium text-[var(--color-brand-indigo)] hover:underline"
          style={font}
          onClick={() => {
            addRecentSearch(query)
            close()
          }}
        >
          View all results for &ldquo;{query}&rdquo;
        </Link>
      </div>
    </div>
  )
}
